import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59);
}

function startOfWeek(d) {
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.getFullYear(), d.getMonth(), diff);
}

function endOfWeek(d) {
    const day = d.getDay();
    const diff = d.getDate() + (6 - day);
    return new Date(d.getFullYear(), d.getMonth(), diff, 23, 59, 59);
}

function addDays(d, n) {
    const result = new Date(d);
    result.setDate(result.getDate() + n);
    return result;
}

function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatISODate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ClassCalendar({ gymId, onClassClick }) {
    const [view, setView] = useState('month'); // 'week' | 'month'
    const [currentDate, setCurrentDate] = useState(new Date());
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(false);

    const range = useMemo(() => {
        if (view === 'week') {
            const start = startOfWeek(currentDate);
            const end = endOfWeek(currentDate);
            return { from: formatISODate(start) + 'T00:00:00', to: formatISODate(end) + 'T23:59:59' };
        }
        const start = startOfMonth(currentDate);
        const end = endOfMonth(currentDate);
        return { from: formatISODate(start) + 'T00:00:00', to: formatISODate(end) + 'T23:59:59' };
    }, [view, currentDate]);

    useEffect(() => {
        setLoading(true);
        api.get('/owner/classes', {
            params: { ...range, per_page: 500, gym_id: gymId },
        })
        .then(res => setClasses(res.data.data || []))
        .catch(() => setClasses([]))
        .finally(() => setLoading(false));
    }, [range, gymId]);

    const classesByDay = useMemo(() => {
        const map = {};
        classes.forEach(cls => {
            if (!cls.starts_at) return;
            const d = new Date(cls.starts_at);
            const key = formatISODate(d);
            if (!map[key]) map[key] = [];
            map[key].push(cls);
        });
        Object.values(map).forEach(list => list.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
        return map;
    }, [classes]);

    const goPrev = () => {
        if (view === 'week') {
            setCurrentDate(d => addDays(d, -7));
        } else {
            setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
        }
    };

    const goNext = () => {
        if (view === 'week') {
            setCurrentDate(d => addDays(d, 7));
        } else {
            setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
        }
    };

    const goToday = () => setCurrentDate(new Date());

    const title = view === 'week'
        ? `Semana del ${startOfWeek(currentDate).getDate()} al ${endOfWeek(currentDate).getDate()} de ${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
        : `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

    const calendarDays = useMemo(() => {
        if (view === 'week') {
            const start = startOfWeek(currentDate);
            return Array.from({ length: 7 }, (_, i) => addDays(start, i));
        }
        const start = startOfMonth(currentDate);
        const startDay = start.getDay();
        const daysInMonth = endOfMonth(currentDate).getDate();
        const days = [];
        for (let i = 0; i < startDay; i++) {
            days.push(null);
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
        }
        return days;
    }, [view, currentDate]);

    const today = new Date();

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={goPrev} className="border-[#3a3a3f] text-white hover:bg-white/5 px-2">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={goToday} className="border-[#3a3a3f] text-white hover:bg-white/5 text-xs">
                        Hoy
                    </Button>
                    <Button variant="outline" size="sm" onClick={goNext} className="border-[#3a3a3f] text-white hover:bg-white/5 px-2">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
                <h2 className="text-sm sm:text-base font-bold uppercase tracking-wide text-white text-center sm:text-left">{title}</h2>
                <div className="flex gap-1">
                    <Button variant={view === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setView('week')} className={view === 'week' ? '' : 'border-[#3a3a3f] text-white hover:bg-white/5'}>
                        <CalendarDays className="h-4 w-4 mr-1" />Semana
                    </Button>
                    <Button variant={view === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setView('month')} className={view === 'month' ? '' : 'border-[#3a3a3f] text-white hover:bg-white/5'}>
                        <Calendar className="h-4 w-4 mr-1" />Mes
                    </Button>
                </div>
            </div>

            {loading ? (
                <p className="text-white/60 text-center py-8">Cargando...</p>
            ) : (
                <>
                    <div className="grid grid-cols-7 gap-px bg-[#3a3a3f] rounded-lg overflow-hidden border border-[#3a3a3f]">
                        {DAYS.map(day => (
                            <div key={day} className="bg-[#0a0a0a] p-2 text-center text-xs font-medium text-white/60 uppercase tracking-wider">
                                {day}
                            </div>
                        ))}
                        {view === 'week' ? (
                            calendarDays.map((day, idx) => {
                                const key = formatISODate(day);
                                const dayClasses = classesByDay[key] || [];
                                const isToday = isSameDay(day, today);
                                return (
                                    <div key={idx} className={`bg-[#0a0a0a] min-h-[120px] p-2 space-y-1 ${isToday ? 'ring-1 ring-inset ring-white/20' : ''}`}>
                                        <div className={`text-xs font-bold mb-1 ${isToday ? 'text-white' : 'text-white/60'}`}>{day.getDate()}</div>
                                        {dayClasses.map(cls => (
                                            <button
                                                key={cls.id}
                                                onClick={() => onClassClick?.(cls)}
                                                className="w-full text-left text-[10px] sm:text-xs bg-white/5 hover:bg-white/10 rounded px-1.5 py-1 text-white truncate transition-colors"
                                                title={cls.name}
                                            >
                                                <span className="text-white/60">{cls.starts_at.slice(11, 16)}</span> {cls.name}
                                            </button>
                                        ))}
                                    </div>
                                );
                            })
                        ) : (
                            calendarDays.map((day, idx) => {
                                if (!day) {
                                    return <div key={idx} className="bg-[#0a0a0a] min-h-[80px] sm:min-h-[100px]" />;
                                }
                                const key = formatISODate(day);
                                const dayClasses = classesByDay[key] || [];
                                const isToday = isSameDay(day, today);
                                return (
                                    <div key={idx} className={`bg-[#0a0a0a] min-h-[80px] sm:min-h-[100px] p-1 sm:p-2 space-y-1 ${isToday ? 'ring-1 ring-inset ring-white/20' : ''}`}>
                                        <div className={`text-xs font-bold mb-1 ${isToday ? 'text-white' : 'text-white/60'}`}>{day.getDate()}</div>
                                        {dayClasses.slice(0, 3).map(cls => (
                                            <button
                                                key={cls.id}
                                                onClick={() => onClassClick?.(cls)}
                                                className="w-full text-left text-[10px] sm:text-xs bg-white/5 hover:bg-white/10 rounded px-1 py-0.5 text-white truncate transition-colors"
                                                title={`${cls.starts_at.slice(11, 16)} ${cls.name}`}
                                            >
                                                {cls.starts_at.slice(11, 16)} {cls.name}
                                            </button>
                                        ))}
                                        {dayClasses.length > 3 && (
                                            <p className="text-[10px] text-white/40 px-1">+{dayClasses.length - 3} más</p>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
